import { ReactNode } from 'react'
import { Box, Skeleton } from '@mui/material'
import { useTranslation } from 'react-i18next'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { colors } from '../../const/colors'

interface StatValueProps {
  loading: boolean
  error?: boolean
  errorMessage?: string
  empty?: boolean
  emptyMessage?: string
  onDark?: boolean
  skeletonColor?: string
  errorColor?: string
  emptyColor?: string
  width?: number
  height?: number
  children: ReactNode
}

const StatValue = ({
  loading,
  error = false,
  errorMessage,
  empty = false,
  emptyMessage,
  onDark = false,
  skeletonColor,
  errorColor,
  emptyColor,
  width = 74,
  height = 30,
  children,
}: StatValueProps) => {
  const { t } = useTranslation(['generic'])

  if (loading) {
    return (
      <Skeleton
        variant="rounded"
        width={width}
        height={height}
        sx={{
          bgcolor: skeletonColor ?? (onDark ? 'rgba(255,255,255,0.22)' : undefined),
          borderRadius: '8px',
        }}
      />
    )
  }

  if (error) {
    return (
      <Box
        title={errorMessage}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          fontSize: '15px',
          fontWeight: 800,
          color: errorColor ?? (onDark ? 'rgba(255,255,255,0.9)' : colors.redDark),
          height,
        }}
      >
        <ErrorOutlineIcon sx={{ fontSize: 18 }} />
        {t('generic:error')}
      </Box>
    )
  }

  if (empty) {
    return (
      <Box
        component="span"
        sx={{ color: emptyColor ?? (onDark ? 'inherit' : colors.textMuted) }}
      >
        {emptyMessage ?? t('generic:none')}
      </Box>
    )
  }

  return <>{children}</>
}

export default StatValue
